import { createContext, use, useCallback, useRef, useState, type ReactNode } from 'react'
import { normalizeText } from '@shared/text/normalize'

/**
 * Audio playback.
 *
 * One shared `<audio>` element rather than one per phrase: a lesson screen can
 * show a dozen playable strings, and mounting a dozen media elements on a
 * phone is wasteful. A single element also gives the behaviour you want for
 * free — starting a new clip stops the previous one.
 *
 * Mounted at the app root so that *every* screen can play a clip. Screens that
 * resolve clips by phrase — the lesson player — nest a second provider
 * carrying their own text→id map; the inner one simply shadows the outer.
 * That costs nothing, because the media element is created lazily on first
 * play, so a provider nothing plays through never allocates one.
 */

/**
 * Replaying the same clip slows it down.
 *
 * 0.7 rather than 0.5: below about 0.65 the AAC clips start to sound smeared
 * and the vowel she is trying to hear stops being the vowel a Brazilian would
 * say. 0.7 is slow enough to separate `avô` from `avó` and still sound like
 * speech.
 */
const SLOW_RATE = 0.7

/**
 * After this long, the next tap starts from normal speed again. Without it,
 * coming back to a word ten minutes later would surprise her with a slow clip
 * and no explanation.
 */
const RATE_RESET_MS = 20_000

interface AudioContextValue {
  /** Clip id for a Portuguese phrase, or null when none has been generated. */
  resolve: (text: string) => string | null
  play: (mediaId: string) => void
  playingId: string | null
  failedId: string | null
  /** Rate the current/last clip played at, so the button can show it. */
  playingRate: number
}

const AudioContext = createContext<AudioContextValue>({
  resolve: () => null,
  play: () => undefined,
  playingId: null,
  failedId: null,
  playingRate: 1,
})

export function AudioProvider({
  audio,
  children,
}: {
  /**
   * `normalizeText(phrase)` → media id, from the lesson payload. Optional:
   * screens that already hold clip ids (flashcards) need playback without a
   * phrase map.
   */
  audio?: Record<string, string> | undefined
  children: ReactNode
}) {
  const elementRef = useRef<HTMLAudioElement | null>(null)
  const [playingId, setPlayingId] = useState<string | null>(null)
  const [failedId, setFailedId] = useState<string | null>(null)
  const [playingRate, setPlayingRate] = useState(1)
  /** Which clip was tapped last, at what rate, and when. */
  const lastRef = useRef<{ id: string; rate: number; at: number } | null>(null)

  const resolve = useCallback(
    (text: string): string | null => audio?.[normalizeText(text)] ?? null,
    [audio],
  )

  const play = useCallback((mediaId: string) => {
    let element = elementRef.current
    if (!element) {
      element = new Audio()
      element.preload = 'none'
      elementRef.current = element
    }

    // Tap the same clip again and it slows down; tap a different one, or come
    // back much later, and it starts from normal again. Alternating rather
    // than latching means a third tap returns to full speed, so she can always
    // hear it said properly without hunting for a control.
    const last = lastRef.current
    const isRepeat =
      last !== null && last.id === mediaId && Date.now() - last.at < RATE_RESET_MS
    const rate = isRepeat && last.rate === 1 ? SLOW_RATE : 1
    lastRef.current = { id: mediaId, rate, at: Date.now() }

    // Reusing one element means a re-tap restarts cleanly instead of stacking.
    element.pause()
    element.onended = () => setPlayingId(null)
    element.onerror = () => {
      setPlayingId(null)
      setFailedId(mediaId)
    }

    element.src = `/media/${mediaId}`
    // Without this the clip is pitch-shifted as well as slowed, which teaches
    // her a vowel nobody actually says. Safari needs the prefixed form.
    element.preservesPitch = true
    ;(element as { webkitPreservesPitch?: boolean }).webkitPreservesPitch = true
    element.playbackRate = rate

    setFailedId(null)
    setPlayingId(mediaId)
    setPlayingRate(rate)

    void element.play().catch(() => {
      // Autoplay policies and missing files both land here. Failing quietly
      // with a visible state beats an unhandled rejection in the console.
      setPlayingId(null)
      setFailedId(mediaId)
    })
  }, [])

  return (
    <AudioContext value={{ resolve, play, playingId, failedId, playingRate }}>
      {children}
    </AudioContext>
  )
}

export function useAudio(): AudioContextValue {
  return use(AudioContext)
}

/**
 * A speaker button for a Portuguese phrase.
 *
 * Renders nothing when the phrase has no clip, so content added before the
 * audio was generated simply appears without a button rather than with a
 * broken one.
 */
export function SpeakButton({
  text,
  size = 'md',
  className = '',
}: {
  text: string
  size?: SpeakSize
  className?: string
}) {
  const { resolve } = useAudio()
  const mediaId = resolve(text)
  if (!mediaId) return null

  return <SpeakButtonById mediaId={mediaId} label={text} size={size} className={className} />
}

export type SpeakSize = 'sm' | 'md' | 'lg'

const BOX: Record<SpeakSize, string> = { sm: 'size-8', md: 'size-10', lg: 'size-11' }

/**
 * The same button, for callers that already hold a media id.
 *
 * The phrasebook and the review session get their clip ids straight from the
 * API rather than resolving them from text, and each had hand-rolled its own
 * speaker button. That meant the slow-replay affordance — the `0.7×` badge and
 * the "tap again" hint — existed only on `SpeakButton`, so those two screens
 * played slowly on a second tap while giving no sign that they would. One
 * implementation is what keeps behaviour and affordance from drifting apart
 * again.
 */
export function SpeakButtonById({
  mediaId,
  label,
  size = 'md',
  className = '',
}: {
  mediaId: string
  /** Spoken text, used only for the accessible name. */
  label: string
  size?: SpeakSize
  className?: string
}) {
  const { play, playingId, failedId, playingRate } = useAudio()
  const text = label

  const isPlaying = playingId === mediaId
  const hasFailed = failedId === mediaId
  const isSlow = isPlaying && playingRate < 1
  const box = BOX[size]

  return (
    <button
      type="button"
      onClick={(event) => {
        // Speaker buttons often sit inside another tappable row (a matching
        // tile, an answer option). Without this, hearing a word would also
        // select it.
        event.preventDefault()
        event.stopPropagation()
        play(mediaId)
      }}
      aria-label={`Listen: ${text}. Tap again to hear it slowly.`}
      className={`relative inline-flex ${box} shrink-0 items-center justify-center rounded-full transition-colors ${
        hasFailed
          ? 'bg-sand-100 text-ink-400'
          : isPlaying
            ? 'bg-brand-600 text-white'
            : 'bg-brand-100 text-brand-600 hover:bg-brand-200'
      } ${className}`}
    >
      <SpeakerIcon animated={isPlaying} />
      {isSlow && (
        <span
          className="absolute -bottom-1 -right-1 rounded-full bg-gold-400 px-1 text-[9px] font-semibold leading-[1.4] text-ink-900"
          aria-hidden="true"
        >
          0.7×
        </span>
      )}
    </button>
  )
}

function SpeakerIcon({ animated }: { animated: boolean }) {
  return (
    <svg
      viewBox="0 0 24 24"
      className="size-[55%]"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.9}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M11 5 6.5 8.75H3.5v6.5h3L11 19V5Z" />
      <path d="M14.5 9.5a3.5 3.5 0 0 1 0 5" className={animated ? 'animate-pulse' : ''} />
      <path d="M17.5 7a7 7 0 0 1 0 10" className={animated ? 'animate-pulse' : ''} />
    </svg>
  )
}
